<template>
	<Modal v-model:visible="visibleRef" :title="modalTitle" :width="650">
		<VerticalLayout>
			<TypographyParagraph>
				{{ descriptionText }}
				<TypographyText v-if="applySwitch" code class="nobr-inline">{{ selectedSubNetworkLabel }}</TypographyText>
			</TypographyParagraph>
			<LabeledSwitch v-if="saveEnabled" v-model="saveSwitch" :rightLabel="$t('cnet-constructor.legend.saveModal.saveSwitchLabel')" />
			<LabeledSwitch v-if="saveEnabled" v-model="applySwitch" :rightLabel="$t('cnet-constructor.legend.saveModal.applySwitchLabel')" />
		</VerticalLayout>
		<DividerAutoDirection v-if="saveEnabled" />
		<Form :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" :colon="false" labelWrap>
			<FormItem v-if="subNetworkSelectVisible" :label="$t('cnet-constructor.legend.saveModal.subNetworkLabel')">
				<Select class="ui-select" v-model:value="applySubNetworkId" :options="subNetworkOptions" :disabled="!applySwitch" />
			</FormItem>
		</Form>
		<SettingsInfoBanner v-if="showCacheWarning" status="warning" class="pd-md-vertical">
			{{ $t('cnet-constructor.legend.saveModal.cacheWarning') }}
		</SettingsInfoBanner>
		<div class="table-container">
			<Table
				:columns="columns"
				:data-source="tableData"
				:pagination="false"
				size="small"
			>
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'cnet_number'">
						<Select
							class="ui-select"
							v-model:value="record.cnet_number"
							:loading="isLoadingCNetList"
							:options="cnetNumberOptions"
							:disabled="!applySwitch"
						/>
					</template>
					<template v-if="column.dataIndex === 'cnet_date'">
						<DatePicker
							class="ui-select"
							v-model:value="record.cnet_date"
							:disabled="!applySwitch"
							format="YYYY-MM-DD"
						/>
					</template>
				</template>
			</Table>
		</div>
		<template #footer>
			<Button key="back" @click="onSaveModalCancel">{{ $t('cnet-constructor.legend.saveModal.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :disabled="!saveSwitch && !applySwitch" @click="onSubmit">
				{{ actionButtonLabel }}
			</Button>
		</template>
	</Modal>
</template>

<script>
import { computed, ref, watch, toRef } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import {
	Button, Select, Modal, TypographyParagraph, TypographyText,
	Form, FormItem, Table, DatePicker, message,
} from 'ant-design-vue';

import Consts from '@/api/Consts';

import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';

import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useCNetNumberSelectData } from '@/components/common/controls/useCNetNumberSelectData';

import { getTodayDayJs, dayJsToString } from '@/components/common/dateUtils';
import { shouldShowCnetCacheWarning } from '@/components/common/cnetPublicationUtils';

export default {
	components: {
		Button, Select, Modal, TypographyParagraph, TypographyText, Form, FormItem, Table, DatePicker,
		DividerAutoDirection, LabeledSwitch, SettingsInfoBanner, VerticalLayout,
	},
	props: {
		visible: {
			type: Boolean,
			required: true,
		},
		forcedSubNetworkId: {
			type: Number,
			default: 0,
		},
		cNetConstructorSaveAndApply: {
			type: Function,
			required: true,
		},
		subGridCount: {
			type: Number,
			required: true,
		},
		saveEnabled: {
			type: Boolean,
			default: true,
		},
		subGridSelectedCounts: {
			type: Array,
			default: () => [],
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const visibleRef = useVModel(props, 'visible', emit);
		const subNetworksStore = useSubNetworksStore();
		const subNetworkOptions = computed(() => subNetworksStore.subNetworkFilterOptions);
		const columns = Consts.applyCnetModalTableColumnsComp;

		const saveEnabledRef = toRef(props, 'saveEnabled');
		const saveSwitch = ref(saveEnabledRef.value);
		const applySwitch = ref(true);

		const modalTitle = computed(() => t('cnet-constructor.legend.saveModal.title.' + (saveEnabledRef.value ? 'saveAndApply' : 'applyOnly')));
		const action = computed(() => {
			if (saveSwitch.value && applySwitch.value) {
				return 'saveAndApply';
			}
			if (saveSwitch.value) {
				return 'saveOnly';
			}
			if (applySwitch.value) {
				return 'applyOnly';
			}
			return 'noAction';
		});

		const descriptionText = computed(() => t('cnet-constructor.legend.saveModal.descriptions.' + action.value));
		const actionButtonLabel = computed(() => t('cnet-constructor.legend.saveModal.buttons.' + action.value));

		const forcedSubNetworkIdRef = toRef(props, 'forcedSubNetworkId');
		const applySubNetworkId = ref(forcedSubNetworkIdRef.value || null);

		const tableData = ref([]);
		const { isLoadingCNetList, cnetNumberOptions, cnetPublications, loadCNetNumbers } = useCNetNumberSelectData(ref(null), () => {
			updateTableData();
		});
		const subGridSelectedCountsRef = toRef(props, 'subGridSelectedCounts');

		const subGridCountRef = toRef(props, 'subGridCount');
		const updateTableData = () => {
			tableData.value = Array.from({ length: subGridCountRef.value }, (_, index) => ({
				key: index,
				subgrid: subGridCountRef.value > 1 ? t('cnet-constructor.legend.step3SubGridLabel', { n: index + 1 }) : t('cnet-constructor.legend.saveModal.thisSubGridLabel'),
				cnet_number: null,
				cnet_date: getTodayDayJs(),
			}));
		};

		watch(visibleRef, value => {
			if (value && subNetworkOptions.value.length > 0) {
				applySubNetworkId.value = forcedSubNetworkIdRef.value > 0 ? forcedSubNetworkIdRef.value : subNetworkOptions.value[0].value;
				loadCNetNumbers();
				saveSwitch.value = saveEnabledRef.value;
				applySwitch.value = true;
			}
		});

		const validateApplyData = () => {
			if (tableData.value.some(row => row.cnet_number == null)) {
				message.error(t('cnet-constructor.legend.saveModal.validation.numbersRequired'));
				return false;
			}
			if (tableData.value.some(row => row.cnet_date == null)) {
				message.error(t('cnet-constructor.legend.saveModal.validation.datesRequired'));
				return false;
			}
			const numbers = tableData.value.map(row => row.cnet_number);
			if (new Set(numbers).size !== numbers.length) {
				message.error(t('cnet-constructor.legend.saveModal.validation.numbersUnique'));
				return false;
			}
			return true;
		};

		const onSaveModalCancel = () => {
			visibleRef.value = false;
		};

		const onSubmit = () => {
			if (!saveSwitch.value && !applySwitch.value) {
				return;
			}
			if (applySwitch.value && !validateApplyData()) {
				return;
			}
			let subNetworkId = null;
			let cnetNumbers = null;
			let cnetDates = null;
			if (applySwitch.value) {
				subNetworkId = applySubNetworkId.value;
				cnetNumbers = tableData.value.map(row => row.cnet_number);
				cnetDates = tableData.value.map(row => dayJsToString(row.cnet_date));
			}
			props.cNetConstructorSaveAndApply(subNetworkId, cnetNumbers, cnetDates, saveSwitch.value, () => onSaveModalCancel());
		};

		const selectedSubNetworkLabel = computed(() => {
			return subNetworkOptions.value.find(option => option.value === applySubNetworkId.value)?.label;
		});
		const subNetworkSelectVisible = computed(() => forcedSubNetworkIdRef.value == 0);

		const showCacheWarning = computed(() => {
			if (!applySwitch.value) {
				return false;
			}
			return tableData.value.some((row, index) => {
				const gridpointCount = subGridSelectedCountsRef.value[index];
				const weekDate = row.cnet_date != null ? dayJsToString(row.cnet_date) : null;
				return shouldShowCnetCacheWarning(
					row.cnet_number,
					weekDate,
					gridpointCount,
					cnetPublications.value,
				);
			});
		});

		return {
			visibleRef,
			saveSwitch,
			applySwitch,
			actionButtonLabel,
			applySubNetworkId,
			selectedSubNetworkLabel,
			subNetworkSelectVisible,
			subNetworkOptions,
			cnetNumberOptions,
			isLoadingCNetList,
			columns,
			tableData,
			onSaveModalCancel,
			onSubmit,
			modalTitle,
			descriptionText,
			showCacheWarning,
		};
	},
};
</script>

<style scoped>
.ui-select {
	min-width: 150px;
}
.table-container {
	margin-top: 16px;
}
</style>
